import { runDeleteAutomationWorkflow } from "@/action-dialog";
import {
  flowScheduleErrorMessage,
  useDeleteFlowSchedule,
  useDuplicateFlowSchedule,
  useRunFlowScheduleNow,
  useUpdateFlowSchedule,
} from "@/api/flowSchedules";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import type { ObjectActionHandlers } from "@/object-actions/types";
import { useOpenAutomation } from "@/panes/automation/useOpenAutomation";
import { route } from "@/utils/route";
import type { FlowSchedule } from "@cohesive/api-client";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export interface AutomationActionHandlerDeps {
  /** Fire a manual run (does not advance the cadence). */
  runNow: () => void;
  /** Open the automation pane (also the row's primary click). */
  edit: () => void;
  /** Clear next_run_at; the cron survives so resume keeps the cadence. */
  pause: () => void;
  /** Re-send the stored cron (server recomputes next run); without one, opens the pane. */
  resume: () => void;
  /** Copy into the source's own location (the copy is created paused). */
  duplicate: () => void;
  /** Navigate to the automation's canvas or workspace. */
  openLocation: () => void;
  deleteAutomation: () => void;
}

/**
 * Default deps wired to the flow-schedule mutations — shared by the rail row
 * and the pane kebab so both surfaces act identically. Accepts `undefined`
 * while the pane's schedule is still loading; every dep no-ops until it lands.
 */
export function useAutomationObjectActionDeps(
  schedule: FlowSchedule | undefined,
  options?: {
    /** The right pane currently shows this automation — close it on delete. */
    isPaneActive?: boolean;
  },
): AutomationActionHandlerDeps {
  const openAutomation = useOpenAutomation();
  const appNavigate = useAppNavigate();
  const navigate = useNavigate();
  const updateSchedule = useUpdateFlowSchedule();
  const duplicateSchedule = useDuplicateFlowSchedule();
  const deleteSchedule = useDeleteFlowSchedule();
  const runNow = useRunFlowScheduleNow();
  const isPaneActive = options?.isPaneActive ?? false;

  return useMemo(
    (): AutomationActionHandlerDeps => ({
      runNow: () => {
        if (!schedule) return;
        runNow.mutate(schedule.id, {
          onSuccess: () => toast.success("Run started."),
          onError: (error) =>
            toast.error(
              flowScheduleErrorMessage(error, "Failed to start the run."),
            ),
        });
      },
      edit: () => {
        if (schedule) openAutomation(schedule.id);
      },
      pause: () => {
        if (!schedule) return;
        updateSchedule.mutate(
          {
            id: schedule.id,
            data: { next_run_at: null },
            analytics: { intent: "toggle", enabled: false },
          },
          {
            onError: (error) =>
              toast.error(
                flowScheduleErrorMessage(error, "Failed to pause automation."),
              ),
          },
        );
      },
      resume: () => {
        if (!schedule) return;
        if (!schedule.cron) {
          // No retained cadence — the pane's schedule section is where one
          // gets picked, so resume routes there instead of a no-op PATCH.
          openAutomation(schedule.id);
          return;
        }
        updateSchedule.mutate(
          {
            id: schedule.id,
            data: { cron: schedule.cron },
            analytics: { intent: "toggle", enabled: true },
          },
          {
            onError: (error) =>
              toast.error(
                flowScheduleErrorMessage(error, "Failed to resume automation."),
              ),
          },
        );
      },
      duplicate: () => {
        if (!schedule) return;
        duplicateSchedule.mutate(
          {
            id: schedule.id,
            // The endpoint requires an explicit target — duplicate in place.
            data: schedule.canvas_id
              ? { canvas_id: schedule.canvas_id }
              : { workspace_id: schedule.workspace_id },
          },
          {
            onSuccess: (copy) => openAutomation(copy.id),
            onError: (error) =>
              toast.error(
                flowScheduleErrorMessage(
                  error,
                  "Failed to duplicate automation.",
                ),
              ),
          },
        );
      },
      openLocation: () => {
        if (schedule?.canvas_id) {
          navigate(
            route(ROUTES.CANVAS.DETAIL, { canvasId: schedule.canvas_id }),
          );
        } else if (schedule?.workspace_id) {
          navigate(
            route(ROUTES.WORKSPACES.DETAIL, {
              workspaceId: schedule.workspace_id,
            }),
          );
        }
      },
      deleteAutomation: () => {
        if (!schedule) return;
        void runDeleteAutomationWorkflow({
          schedule,
          deleteFlowSchedule: deleteSchedule,
          onSuccess: () => {
            if (isPaneActive) appNavigate({ pane: "rightPane", to: null });
          },
        });
      },
    }),
    [
      appNavigate,
      deleteSchedule,
      duplicateSchedule,
      isPaneActive,
      navigate,
      openAutomation,
      runNow,
      schedule,
      updateSchedule,
    ],
  );
}

/** Registry handlers for an automation row / pane kebab. */
export function useAutomationObjectActionHandlers(
  deps: AutomationActionHandlerDeps,
): ObjectActionHandlers {
  return useMemo(
    (): ObjectActionHandlers => ({
      "run-now": () => window.setTimeout(deps.runNow, 0),
      edit: () => window.setTimeout(deps.edit, 0),
      pause: () => window.setTimeout(deps.pause, 0),
      resume: () => window.setTimeout(deps.resume, 0),
      duplicate: () => window.setTimeout(deps.duplicate, 0),
      "open-location": () => window.setTimeout(deps.openLocation, 0),
      delete: () => window.setTimeout(deps.deleteAutomation, 0),
    }),
    [deps],
  );
}
